import { useCallback, useEffect, useState } from 'react';
import { Button, Checkbox, Input, Textarea } from '@heroui/react';
import { useAuth, useUserAccess } from '@/hooks/auth';
import { useConfig } from '@/hooks/config';
import { api, makePayloadRequestInit } from '@/lib/api';
import { NoAccess } from '@/components/common/noaccess';

interface Policy {
  allowedMembershipValues: string[];
  nonMemberMessage: string;
  nonMemberLinkTitle: string;
  nonMemberRedirectUrl: string;
}

export default function MemberGatewaySettings() {
  const access = useUserAccess();
  const { firebaseUser } = useAuth();
  const {
    api: { baseUrl },
  } = useConfig();
  const [selected, setSelected] = useState<string[]>([]);
  const [nonMemberMessage, setNonMemberMessage] = useState('');
  const [nonMemberLinkTitle, setNonMemberLinkTitle] = useState('');
  const [nonMemberRedirectUrl, setNonMemberRedirectUrl] = useState('');
  const [values, setValues] = useState<string[]>([]);
  const [saved, setSaved] = useState<Policy | null>(null);
  const [loading, setLoading] = useState(true);
  const [ready, setReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const request = useCallback(
    async (path: string, init: RequestInit = {}) => {
      const response = await api(
        `website/member-settings${path}`,
        firebaseUser || null,
        init,
        { baseUrl },
      );
      if (!response.ok) {
        throw new Error(
          'The membership settings could not be loaded or saved. Please try again.',
        );
      }

      return response.json();
    },
    [firebaseUser, baseUrl],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    setNotice('');
    setReady(false);
    try {
      const [settings, options] = await Promise.all([
        request(''),
        request('/values'),
      ]);
      const policy = settings.policy as Policy | null;
      setSaved(policy);
      setValues(options.values);
      setSelected(policy?.allowedMembershipValues || []);
      setNonMemberMessage(policy?.nonMemberMessage || '');
      setNonMemberLinkTitle(policy?.nonMemberLinkTitle || '');
      setNonMemberRedirectUrl(policy?.nonMemberRedirectUrl || '');
      setReady(true);
    } catch {
      setError(
        'Unable to load membership settings and PCO values. Please try again.',
      );
    } finally {
      setLoading(false);
    }
  }, [request]);

  useEffect(() => {
    if (access?.admin && firebaseUser) {
      void load();
    }
  }, [access?.admin, firebaseUser, load]);

  async function save() {
    if (
      Boolean(nonMemberLinkTitle.trim()) !==
      Boolean(nonMemberRedirectUrl.trim())
    ) {
      setError('Add both a guidance link title and URL, or leave both blank.');

      return;
    }

    setSaving(true);
    setError('');
    setNotice('');
    try {
      const result = await request(
        '',
        makePayloadRequestInit(
          {
            allowedMembershipValues: selected,
            nonMemberMessage,
            nonMemberLinkTitle,
            nonMemberRedirectUrl,
          },
          'PUT',
        ),
      );
      setSaved(result.policy);
      setNotice(
        'Saved. The rule applies on each member link request, including existing sessions.',
      );
    } catch {
      setError(
        'The rule could not be saved. Your changes are still here; please try again.',
      );
    } finally {
      setSaving(false);
    }
  }

  if (!access?.admin) {
    return <NoAccess />;
  }

  const choices = [
    ...new Set([...values, ...(saved?.allowedMembershipValues || [])]),
  ].sort();

  return (
    <section
      className='mx-auto w-full max-w-2xl space-y-6 p-4 md:p-8'
      aria-labelledby='member-gateway-title'
    >
      <div>
        <h1 id='member-gateway-title' className='text-2xl font-semibold'>
          Member link access
        </h1>
        <p className='mt-2 text-default-600'>
          Choose which Planning Center membership values can open links for
          events in the Members Only category.
        </p>
      </div>
      {error && (
        <p role='alert' className='rounded-lg bg-danger-50 p-3 text-danger-700'>
          {error}
        </p>
      )}
      {notice && (
        <p
          role='status'
          className='rounded-lg bg-success-50 p-3 text-success-700'
        >
          {notice}
        </p>
      )}
      {loading ? (
        <p role='status'>Loading PCO membership values…</p>
      ) : (
        <>
          {!saved && ready && (
            <p className='rounded-lg bg-warning-50 p-3 text-warning-800'>
              Member link access is closed until you save a membership rule.
            </p>
          )}
          <fieldset disabled={!ready || saving} className='space-y-3'>
            <legend className='mb-2 font-medium'>
              Values that grant member access
            </legend>
            {choices.map((value) => (
              <div key={value}>
                <Checkbox
                  isSelected={selected.includes(value)}
                  isDisabled={saving || !ready}
                  onValueChange={(checked) => {
                    setSelected((current) =>
                      checked
                        ? [...current, value]
                        : current.filter((item) => item !== value),
                    );
                    setNotice('');
                  }}
                >
                  {value}
                  {!values.includes(value) && (
                    <span className='ml-2 text-sm text-default-500'>
                      (saved; no current PCO records)
                    </span>
                  )}
                </Checkbox>
              </div>
            ))}
            {!choices.length && ready && (
              <p className='text-default-500'>
                No membership values were found in the synced PCO people. Check
                the PCO sync, then reload.
              </p>
            )}
          </fieldset>
          {ready && selected.length === 0 && (
            <p className='text-warning-700'>
              With no values selected, everyone is denied member link access.
            </p>
          )}
          <p className='text-sm text-default-500'>
            Membership is checked whenever a protected link is opened. PCO
            changes take effect once synced to MinistryHub.
          </p>
          <fieldset
            disabled={!ready || saving}
            className='space-y-4 rounded-xl border border-default-200 p-4'
          >
            <legend className='px-2 font-medium'>
              Guidance for people without member access
            </legend>
            <Textarea
              label='Guidance message'
              description='Shown after the person verifies their email. A generic member-access message is used when this is blank.'
              value={nonMemberMessage}
              maxLength={2000}
              minRows={3}
              isDisabled={saving || !ready}
              onValueChange={(value) => {
                setNonMemberMessage(value);
                setNotice('');
              }}
            />
            <Input
              label='Guidance link title'
              description='Required when a guidance URL is provided.'
              value={nonMemberLinkTitle}
              maxLength={120}
              isDisabled={saving || !ready}
              isInvalid={
                Boolean(nonMemberRedirectUrl.trim()) &&
                !nonMemberLinkTitle.trim()
              }
              onValueChange={(value) => {
                setNonMemberLinkTitle(value);
                setNotice('');
              }}
            />
            <Input
              type='url'
              label='Guidance URL'
              description='Use a complete HTTPS URL. The link appears only when both fields are set.'
              placeholder='https://vivechurch.org/...'
              value={nonMemberRedirectUrl}
              maxLength={2048}
              isDisabled={saving || !ready}
              isInvalid={
                Boolean(nonMemberLinkTitle.trim()) &&
                !nonMemberRedirectUrl.trim()
              }
              onValueChange={(value) => {
                setNonMemberRedirectUrl(value);
                setNotice('');
              }}
            />
          </fieldset>
        </>
      )}
      <div className='flex gap-3'>
        <Button
          color='primary'
          isLoading={saving}
          isDisabled={!ready || loading}
          onPress={() => void save()}
        >
          Save membership rule
        </Button>
        <Button
          variant='bordered'
          isDisabled={loading || saving}
          onPress={() => void load()}
        >
          Reload
        </Button>
      </div>
    </section>
  );
}
